/**
 * API Service
 * Requirements: 14.2
 */
import axios, { AxiosInstance, AxiosError } from 'axios';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import { apiConfig, isLocalMode } from '../config/amplify';
import type {
  Resume,
  ResumeJSON,
  Recommendation,
  UserJob,
  JobListResponse,
  ResumeListResponse,
  GenerationStatusResponse,
  UserPreferences,
  SubcomponentType,
  GenerationType,
  JobPhase,
  ImportUrlResponse,
  ImportProcessResponse,
  JoinOrganizationResponse,
  ApplicationAnswer,
  ApplicationAnswersListResponse,
  InterviewQuestion,
  InterviewUserData,
  InterviewListResponse,
  CreateJobFromUrlResponse,
  CreateJobManualResponse,
  WritingFingerprint,
  CalibrateRoundResult,
  DedicatedMaster,
  DedicatedMasterSearchResponse,
  DedicatedMasterStatusResponse,
  DedicatedMasterContentResponse,
  RecruiterCheckResponse,
  SummarySplitProposal,
} from '../types';
import type { HelpTopic } from '../help/types';
import type { LintFinding } from '../utils/scorecard';

class ApiService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: apiConfig.baseUrl,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add auth token to requests
    this.client.interceptors.request.use(async (config) => {
      if (isLocalMode) {
        // Local mode: send user identity via headers instead of JWT
        const userId = localStorage.getItem('local_user_id') || '';
        const userEmail = localStorage.getItem('local_user_email') || '';
        if (userId) {
          config.headers['X-Local-User-Id'] = userId;
          config.headers['X-Local-User-Email'] = userEmail;
        }
        return config;
      }
      try {
        const session = await fetchAuthSession();
        const token = session.tokens?.idToken?.toString();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch (error) {
        console.error('Failed to get auth token:', error);
      }
      return config;
    });

    // Handle 401 (invalid/expired token) - sign out and redirect to login
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        if (isLocalMode) return Promise.reject(error);
        if (error.response?.status === 401) {
          try {
            await signOut();
          } catch {
            // Clear any remaining auth state manually
            localStorage.clear();
            sessionStorage.clear();
          }
          window.location.href = '/';
        }
        return Promise.reject(error);
      }
    );
  }

  // Resume endpoints
  async listResumes(): Promise<ResumeListResponse> {
    const response = await this.client.get('/resumes');
    return response.data;
  }

  async getResume(resumename: string): Promise<Resume> {
    const response = await this.client.get(`/resumes/${encodeURIComponent(resumename)}`);
    return response.data.resume;
  }

  async createResume(resumename: string, resumejson: ResumeJSON, publishMasterResume?: boolean): Promise<Resume> {
    const body: Record<string, unknown> = { resumename, resumejson };
    if (publishMasterResume !== undefined) {
      body.publish_master_resume = publishMasterResume;
    }
    const response = await this.client.post('/resumes', body);
    return response.data.resume;
  }

  /**
   * `publishMasterResume` is sent ONLY on an explicit "Publish now" — it makes
   * this resume the one shown at the user's root URL and clears the marker on
   * every sibling. A normal save omits it, so the server re-publishes the root
   * page only when this resume already owns it.
   */
  async updateResume(resumename: string, resumejson: ResumeJSON, newresumename?: string, publishMasterResume?: boolean): Promise<Resume> {
    const body: Record<string, unknown> = { resumejson };
    if (newresumename && newresumename !== resumename) {
      body.newresumename = newresumename;
    }
    if (publishMasterResume !== undefined) {
      body.publish_master_resume = publishMasterResume;
    }
    const response = await this.client.put(`/resumes/${encodeURIComponent(resumename)}`, body);
    return response.data.resume;
  }

  async deleteResume(resumename: string): Promise<void> {
    await this.client.delete(`/resumes/${encodeURIComponent(resumename)}`);
  }

  // Dedicated (role-targeted) master resumes.
  // The list itself rides along on getResume as `dedicated_masters`; these
  // endpoints drive the add flow and the per-row actions. All are gated
  // server-side on status=='active' (403 for everyone else).

  /**
   * Start the deterministic job-title search + tag aggregation. Returns as soon
   * as the async worker is dispatched — `getDedicatedMasterStatus` reports when
   * the aggregation lands.
   */
  async searchDedicatedMaster(resumename: string, keywords: string): Promise<DedicatedMasterSearchResponse> {
    const response = await this.client.post(
      `/resumes/${encodeURIComponent(resumename)}/dedicated-masters/search`,
      { keywords },
    );
    return response.data;
  }

  async getDedicatedMasterStatus(resumename: string, jobid: string): Promise<DedicatedMasterStatusResponse> {
    const response = await this.client.get(
      `/resumes/${encodeURIComponent(resumename)}/dedicated-masters/status`,
      { params: { jobid } },
    );
    return response.data;
  }

  /**
   * The same status route with `content=1`: status fields PLUS the FROZEN
   * content of that role-targeted resume's last generation (score prefixes
   * already stripped server-side). Drives the Edit Resume preview schematic —
   * it never triggers a generation, and `content` is null when the generated
   * resume could not be read back.
   */
  async getDedicatedMasterContent(
    resumename: string, jobid: string,
  ): Promise<DedicatedMasterContentResponse> {
    const response = await this.client.get(
      `/resumes/${encodeURIComponent(resumename)}/dedicated-masters/status`,
      { params: { jobid, content: 1 } },
    );
    return response.data;
  }

  async createDedicatedMaster(
    resumename: string,
    payload: { jobid: string; path: string; keywords: string },
  ): Promise<DedicatedMaster> {
    const response = await this.client.post(
      `/resumes/${encodeURIComponent(resumename)}/dedicated-masters`,
      payload,
    );
    return response.data.dedicated_master;
  }

  async regenerateDedicatedMaster(resumename: string, dmId: string): Promise<DedicatedMaster> {
    const response = await this.client.post(
      `/resumes/${encodeURIComponent(resumename)}/dedicated-masters/${encodeURIComponent(dmId)}/regenerate`,
    );
    return response.data.dedicated_master;
  }

  /**
   * Edit a role-targeted resume's targeting criteria. The new keywords are
   * only applied to the published resume by a REGENERATE — this just stores
   * them (the editor prompts for the rebuild).
   */
  async updateDedicatedMasterKeywords(
    resumename: string, dmId: string, keywords: string,
  ): Promise<DedicatedMaster> {
    const response = await this.client.put(
      `/resumes/${encodeURIComponent(resumename)}/dedicated-masters/${encodeURIComponent(dmId)}`,
      { keywords },
    );
    return response.data.dedicated_master;
  }

  /** Returns the entries that remain on the resume after the delete. */
  async deleteDedicatedMaster(resumename: string, dmId: string): Promise<DedicatedMaster[]> {
    const response = await this.client.delete(
      `/resumes/${encodeURIComponent(resumename)}/dedicated-masters/${encodeURIComponent(dmId)}`,
    );
    return response.data.dedicated_masters || [];
  }

  /** Modal Cancel: drop a synthetic job whose search was never saved. */
  async cancelDedicatedMasterSearch(resumename: string, jobid: string): Promise<void> {
    await this.client.delete(
      `/resumes/${encodeURIComponent(resumename)}/dedicated-masters/search/${encodeURIComponent(jobid)}`,
    );
  }

  /**
   * Start the async "implied facts" extraction for a resume. Fired when the
   * Edit Resume lightbox CLOSES (users Save many times but Close once).
   *
   * The endpoint only DISPATCHES — it returns immediately with one of:
   *   'started'    a fresh run is underway (poll getResume for the result)
   *   'extracting' an identical run is already in flight (stampede guard)
   *   'unchanged'  a completed run already covers this exact resume content
   *   'unavailable' the worker could not be dispatched
   * There is no status endpoint: `implied_facts_state` rides along on getResume.
   */
  async triggerImpliedFacts(
    resumename: string,
  ): Promise<{ status: 'started' | 'extracting' | 'unchanged' | 'unavailable'; run_id?: string }> {
    const response = await this.client.post(
      `/resumes/${encodeURIComponent(resumename)}/implied-facts`,
    );
    return response.data;
  }

  async reviewResume(resumename: string): Promise<{ recommendations: Recommendation[] }> {
    const response = await this.client.post(`/resumes/${encodeURIComponent(resumename)}/review`);
    return response.data;
  }

  /**
   * Deterministic resume lint (spelling, doubled words, tense drift, duplicate
   * skills…). Same endpoint as reviewResume but `mode: 'lint'` — no LLM, no
   * usage meter, available to every signed-in user regardless of status.
   * `resumejson` is sent so unsaved editor edits are linted.
   */
  async lintResume(
    resumename: string,
    resumejson: ResumeJSON,
    customDictionary?: string[] | null,
  ): Promise<{ findings: LintFinding[]; lint_version: string }> {
    const response = await this.client.post(
      `/resumes/${encodeURIComponent(resumename)}/review`,
      // `custom_dictionary` is sent ONLY when the caller has a freshly-written
      // list on hand (right after "Add to My Dictionary"), so the re-lint can't
      // race the preferences write. Omitted otherwise — the server falls back to
      // the user's stored preferences.
      customDictionary && customDictionary.length
        ? { mode: 'lint', resumejson, custom_dictionary: customDictionary }
        : { mode: 'lint', resumejson },
    );
    return response.data;
  }

  /**
   * Summary v2 assisted migration ("Split into sections"). Same endpoint as
   * reviewResume but `mode: 'split_summary'` — one LLM call that decomposes the
   * resume's saved `summary` + `summary_phrases` into proposed identity /
   * credibility / relevance entries. Read-only server-side: the response is
   * PRE-FILLED into the editor and only persists when the user Saves. Gated on
   * status==='active' like the AI review (403 otherwise).
   */
  async splitSummarySections(resumename: string): Promise<SummarySplitProposal> {
    const response = await this.client.post(
      `/resumes/${encodeURIComponent(resumename)}/review`,
      { mode: 'split_summary' },
    );
    return response.data;
  }

  /**
   * Summary v2 bootstrap ("Auto-generate Summary Fragments"). Same route, same
   * gate, same proposal shape as splitSummarySections — but the server drafts
   * from the WHOLE saved resume (experience, skills, credentials) instead of
   * the legacy summary, so it works for a resume with no summary to split.
   * Read-only server-side: the response is PRE-FILLED into the editor and only
   * persists when the user Saves.
   */
  async bootstrapSummaryFragments(resumename: string): Promise<SummarySplitProposal> {
    const response = await this.client.post(
      `/resumes/${encodeURIComponent(resumename)}/review`,
      { mode: 'summary_fragments' },
    );
    return response.data;
  }

  /**
   * Render the full, unfiltered resume (every section, every item — no
   * page-fit pruning) as a PDF or plain-text file. Returns the public URL of
   * the generated file. `resumejson` is sent so the export reflects the
   * current Edit-Resume contents (saved or not).
   */
  async exportFullResume(format: 'pdf' | 'text', resumename: string, resumejson: ResumeJSON): Promise<{ publicUrl: string }> {
    const response = await this.client.post(`/resumes/export?format=${format}`, { resumename, resumejson });
    return response.data;
  }

  // Job endpoints
  async listJobs(
    phase?: JobPhase,
    filter?: 'active' | 'all',
    page?: { limit: number; cursor?: string },
  ): Promise<JobListResponse> {
    const params = new URLSearchParams();
    if (phase) params.append('phase', phase);
    if (filter) params.append('filter', filter);
    if (page) {
      params.append('limit', String(page.limit));
      if (page.cursor) params.append('cursor', page.cursor);
    }
    const response = await this.client.get(`/jobs?${params.toString()}`);
    return response.data;
  }

  // Phase counts only — one cheap projected query server-side. Used to pick the
  // default phase before fetching any job rows, and by the background poll.
  async getJobCounts(): Promise<JobListResponse> {
    const response = await this.client.get('/jobs?counts_only=1');
    return response.data;
  }

  async getJob(jobid: string): Promise<UserJob> {
    const response = await this.client.get(`/jobs/${jobid}`);
    return response.data.job;
  }

  async createJobManual(data: {
    jobcompany: string;
    jobtitle: string;
    jobdesc: string;
    resumeid?: string;
    joblocation?: string;
    jobsalary?: string;
    jobposteddate?: string;
    joburl?: string;
    jobtags?: string[];
  }): Promise<CreateJobManualResponse> {
    const response = await this.client.post('/jobs/manual', data);
    return response.data;
  }

  async createJobFromUrl(url: string, resumeid?: string): Promise<CreateJobFromUrlResponse> {
    const response = await this.client.post('/jobs/url', { url, resumeid });
    return response.data;
  }

  /**
   * Move a card to a new phase.
   *
   * `recruiterAck` rides along ONLY when the user clicked "Apply anyway" on the
   * Recruiter Check confirm (BR-7): the server stores `recruiter_ack='1'` on the
   * user-job so the same card never asks again. Omitted on every other move.
   */
  async updateJobPhase(jobid: string, phase: JobPhase, recruiterAck?: boolean): Promise<UserJob> {
    const body: Record<string, unknown> = { phase };
    if (recruiterAck) body.recruiter_ack = '1';
    const response = await this.client.put(`/jobs/${jobid}/phase`, body);
    return response.data.userJob;
  }

  /**
   * Recruiter Check (BR-9): recompute the full findings for this job's ALREADY
   * RENDERED resume. The stored `output_quality` carries only the compact grade
   * summary, so the lightbox fetches this on open.
   *
   * Returns 200 for every authenticated user. `status: 'no_files'` is a normal
   * outcome (nothing rendered to check) — never treat it as an error.
   */
  async recheckQuality(jobid: string): Promise<RecruiterCheckResponse> {
    const response = await this.client.post(`/jobs/${jobid}/quality-recheck`);
    return response.data;
  }

  async deleteJob(jobid: string): Promise<void> {
    await this.client.delete(`/jobs/${jobid}`);
  }

  async updateJobDescription(jobid: string, jobdesc: string): Promise<UserJob> {
    const response = await this.client.put(`/jobs/${jobid}/description`, { jobdesc });
    return response.data.job;
  }

  async updateJobDetails(
    jobid: string,
    updates: {
      jobdesc?: string;
      jobtitle?: string;
      jobcompany?: string;
      joblocation?: string;
      jobsalary?: string;
      jobtags?: string[];
      jobreqid?: string;
      super_tags?: string[];
      /** Per-user note — always persisted on the user-job record. */
      jobnotes?: string;
    }
  ): Promise<UserJob> {
    const response = await this.client.put(`/jobs/${jobid}/description`, updates);
    return response.data.job;
  }

  async submitFeedback(jobid: string, topic: 'resume' | 'coverletter', result: 'U' | 'D'): Promise<{ message: string }> {
    const response = await this.client.post(`/jobs/${jobid}/feedback`, { feedback_topic: topic, result });
    return response.data;
  }

  // General app feedback from the "Provide Feedback" lightbox (user context menu)
  async submitAppFeedback(payload: {
    area: string;
    sentiment: string;
    message: string;
    feeling?: string;
    app_rating?: string;
    allow_followup: boolean;
  }): Promise<{ message: string; feedback_id: string }> {
    const response = await this.client.post('/feedback', payload);
    return response.data;
  }

  async updateJobTags(jobid: string, jobtags: string[]): Promise<UserJob> {
    const response = await this.client.put(`/jobs/${jobid}/description`, { jobtags });
    return response.data.job;
  }

  // Generation endpoints
  /**
   * Trigger Phase 1 generation for a job.
   *
   * For trial/hibernating users on a not-yet-enabled job, the backend will
   * atomically consume one `free_gen_remaining` point and set `is_enabled=true`.
   * Returns 402 (`{ error: 'no_free_generations' }`) when the user is out of
   * points and the job has not already been enabled. Callers should catch this
   * and prompt for upgrade rather than treating it as a generic error.
   */
  async generateAll(jobid: string, components?: SubcomponentType[], resumeid?: string): Promise<{ message: string; status: string }> {
    const body: Record<string, SubcomponentType[] | string> = {};
    if (components) body.components = components;
    if (resumeid) body.resumeid = resumeid;
    const response = await this.client.post(`/jobs/${jobid}/generate-all`, Object.keys(body).length > 0 ? body : undefined);
    return response.data;
  }

  async generateSingle(jobid: string, component: SubcomponentType, resumeid?: string): Promise<{ message: string; status: string }> {
    const body = resumeid ? { resumeid } : undefined;
    const response = await this.client.post(`/jobs/${jobid}/generate/${component}`, body);
    return response.data;
  }

  async getGenerationStatus(jobid: string): Promise<GenerationStatusResponse> {
    const response = await this.client.get(`/jobs/${jobid}/status`);
    return response.data;
  }

  async toggleGenerationType(jobid: string, component: SubcomponentType, type: GenerationType): Promise<void> {
    await this.client.put(`/jobs/${jobid}/type/${component}`, { type });
  }

  async updateSubcomponentContent(jobid: string, component: SubcomponentType, content: string): Promise<void> {
    await this.client.put(`/jobs/${jobid}/subcomponent/${component}`, { content });
  }

  // Final file endpoints
  async generateFinalHtml(jobid: string, type: 'resume' | 'cover'): Promise<{ s3Uri: string; publicUrl: string }> {
    const endpoint = type === 'resume' ? 'resume-html' : 'cover-html';
    const response = await this.client.post(`/jobs/${jobid}/final/${endpoint}?type=${type}`);
    return response.data;
  }

  async generateFinalPdf(jobid: string, type: 'resume' | 'cover'): Promise<{ s3Uri: string; publicUrl: string }> {
    const endpoint = type === 'resume' ? 'resume-pdf' : 'cover-pdf';
    const response = await this.client.post(`/jobs/${jobid}/final/${endpoint}?type=${type}`);
    return response.data;
  }

  async generateWorkdayPdf(jobid: string): Promise<{ s3Uri: string; publicUrl: string }> {
    const response = await this.client.post(`/jobs/${jobid}/final/resume-pdf?type=workday`);
    return response.data;
  }

  async generateFinalFiles(jobid: string, type?: 'resume' | 'cover'): Promise<GenerateFinalFilesResponse> {
    const url = type ? `/jobs/${jobid}/generate-final?type=${type}` : `/jobs/${jobid}/generate-final`;
    const response = await this.client.post(url);
    return response.data;
  }

  // Preferences endpoints
  async getPreferences(): Promise<UserPreferences> {
    const response = await this.client.get('/preferences');
    return response.data.preferences;
  }

  async updatePreferences(preferences: Partial<UserPreferences>): Promise<void> {
    await this.client.put('/preferences', { preferences });
  }

  // Writing Style ("Voice Fingerprint") endpoints. The raw sample leaves the
  // browser only inside this request body; only the abstract fingerprint is
  // stored server-side (never the sample). See features/user_writing_style.md.
  async analyzeWritingSample(sample: string, promptId?: string):
    Promise<{ fingerprint: WritingFingerprint; directive_preview: string }> {
    // prompt_id is optional metadata — which starter prompt (if any) the user
    // followed has no effect on the analysis.
    const response = await this.client.post(
      '/writing-style',
      promptId ? { sample, prompt_id: promptId } : { sample },
    );
    return response.data;
  }

  async deleteWritingFingerprint(): Promise<void> {
    await this.client.delete('/writing-style');
  }

  // Calibration ("fine-tune") — multiplexed on POST /writing-style via `action`.
  // Both require an existing fingerprint (server 400s otherwise). No sample.
  async calibrateRound(trait: string): Promise<CalibrateRoundResult> {
    const response = await this.client.post('/writing-style', {
      action: 'calibrate_round',
      trait,
    });
    return response.data;
  }

  async calibratePick(
    trait: string,
    winnerValue: string,
  ): Promise<{ fingerprint: WritingFingerprint; directive_preview: string }> {
    const response = await this.client.post('/writing-style', {
      action: 'calibrate_pick',
      trait,
      winner_value: winnerValue,
    });
    return response.data;
  }

  // Forwarding emails endpoints
  async getForwardingEmails(): Promise<{ forwarding_emails: Array<{ email: string; added_at: string }>; pending_email: string | null; max_emails: number }> {
    const response = await this.client.get('/forwarding-emails');
    return response.data;
  }

  async addForwardingEmail(email: string): Promise<{ message: string; pending_email: string }> {
    const response = await this.client.post('/forwarding-emails', { email, action: 'add' });
    return response.data;
  }

  async verifyForwardingEmail(code: string): Promise<{ message: string; forwarding_emails: Array<{ email: string; added_at: string }> }> {
    const response = await this.client.post('/forwarding-emails', { action: 'verify', code });
    return response.data;
  }

  async resendForwardingCode(): Promise<{ message: string }> {
    const response = await this.client.post('/forwarding-emails', { action: 'resend' });
    return response.data;
  }

  async removeForwardingEmail(email: string): Promise<{ message: string; forwarding_emails: Array<{ email: string; added_at: string }> }> {
    const response = await this.client.delete('/forwarding-emails', { data: { email } });
    return response.data;
  }

  // Resume Import endpoints
  async getImportUrl(filename: string, contentType: string): Promise<ImportUrlResponse> {
    const response = await this.client.post('/resumes/import/url', { filename, contentType });
    return response.data;
  }

  async processImport(s3Key: string): Promise<{ processId: string }> {
    const response = await this.client.post('/resumes/import/process', { s3Key });
    return response.data;
  }

  async getImportStatus(processId: string): Promise<ImportProcessResponse & { status: string; message?: string }> {
    const response = await this.client.get('/resumes/import/status', { params: { processId } });
    return response.data;
  }

  // Admin endpoints
  async getAdminMetrics(): Promise<AdminMetricsResponse> {
    const response = await this.client.get('/admin/metrics');
    return response.data.metrics;
  }

  // Authorization check endpoint
  async checkAuthorization(): Promise<{ authorization: Record<string, unknown>; counts: { jobs: number; resumes: number } }> {
    const response = await this.client.get('/auth/check');
    return response.data;
  }

  // Help / context-aware assistant endpoints
  async getHelpTopics(): Promise<HelpTopic[]> {
    const response = await this.client.get('/help/topics');
    return response.data.topics;
  }

  async adminListHelpTopics(): Promise<HelpTopic[]> {
    const response = await this.client.get('/admin/help');
    return response.data.topics;
  }

  async adminSaveHelpTopic(topicId: string, topic: Partial<HelpTopic>): Promise<{ topic: HelpTopic }> {
    const response = await this.client.put(`/admin/help/${encodeURIComponent(topicId)}`, topic);
    return response.data;
  }

  async adminDeleteHelpTopic(topicId: string): Promise<{ message: string }> {
    const response = await this.client.delete(`/admin/help/${encodeURIComponent(topicId)}`);
    return response.data;
  }

  async adminSeedHelpTopics(overwrite = false): Promise<{ created: number; skipped: number; message: string }> {
    const response = await this.client.post('/admin/help/seed', { overwrite });
    return response.data;
  }

  // Organization endpoints
  async joinOrganization(code: string): Promise<JoinOrganizationResponse> {
    const response = await this.client.post('/organization/join', { code });
    return response.data;
  }

  // Stripe endpoints
  async createCheckoutSession(): Promise<{ checkoutUrl: string; sessionId: string; lifetime?: boolean; free_plan?: boolean; message?: string }> {
    const response = await this.client.post('/stripe/checkout');
    return response.data;
  }

  async createSubscription(priceTier: 'standard' | 'snooze' = 'standard'): Promise<{
    clientSecret?: string;
    subscriptionId?: string;
    free_plan?: boolean;
    tier_changed?: boolean;
    price_tier?: string;
    message?: string;
  }> {
    const response = await this.client.post('/stripe/subscribe', { price_tier: priceTier });
    return response.data;
  }

  async getCustomerPortalUrl(): Promise<{ portalUrl: string }> {
    const response = await this.client.post('/stripe/portal');
    return response.data;
  }

  // Admin - User Management
  async listUsers(search?: string, limit?: number): Promise<{ users: AdminUserListItem[]; count: number; total: number }> {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    if (limit) params.append('limit', limit.toString());
    const response = await this.client.get(`/admin/users?${params.toString()}`);
    return response.data;
  }

  async getUser(userid: string): Promise<AdminUserDetail> {
    const response = await this.client.get(`/admin/users/${userid}`);
    return response.data;
  }

  /** Same record as getUser plus Stripe billing + a 30-day activity rollup.
   *  Deliberately a SEPARATE call: it is materially more expensive, so it runs
   *  once on mount rather than on every auto-refresh poll. */
  async getUserExtended(userid: string): Promise<AdminUserDetailExtended> {
    const response = await this.client.get(`/admin/users/${userid}?extended=1`);
    return response.data;
  }

  async updateUser(userid: string, updates: Record<string, string | boolean>): Promise<{ message: string }> {
    const response = await this.client.put(`/admin/users/${userid}`, updates);
    return response.data;
  }

  async getAiMetrics(): Promise<AiMetricsResponse> {
    const response = await this.client.get('/admin/ai-metrics');
    return response.data;
  }

  async getAiUsageData(months: number = 1): Promise<AiUsageResponse> {
    const response = await this.client.get('/admin/ai-usage-data', { params: { months } });
    return response.data;
  }

  // Admin - Marketing
  async getMarketingData(): Promise<{ campaigns: MarketingCampaignData[] }> {
    const response = await this.client.get('/admin/marketing');
    return response.data;
  }

  async createMarketingItem(item: MarketingItemPayload): Promise<{ message: string }> {
    const response = await this.client.post('/admin/marketing', item);
    return response.data;
  }

  async updateMarketingItem(itemId: string, updates: Record<string, string>): Promise<{ message: string }> {
    const response = await this.client.put(`/admin/marketing/${itemId}`, updates);
    return response.data;
  }

  async deleteMarketingItem(itemId: string): Promise<{ message: string }> {
    const response = await this.client.delete(`/admin/marketing/${itemId}`);
    return response.data;
  }

  async seedMarketingData(data: { campaigns: unknown[] }): Promise<{ message: string }> {
    const response = await this.client.post('/admin/marketing/seed', data);
    return response.data;
  }

  // Admin - AI Model Management
  async listModels(): Promise<{ models: Array<{ model_id: string; display_name: string; enabled: boolean; model_type?: string }> }> {
    const response = await this.client.get('/admin/models');
    return response.data;
  }

  async updateModel(modelId: string, data: Record<string, string | boolean | number | null>): Promise<void> {
    await this.client.put(`/admin/models/${encodeURIComponent(modelId)}`, data);
  }

  async deleteModel(modelId: string): Promise<void> {
    await this.client.delete(`/admin/models/${encodeURIComponent(modelId)}`);
  }

  async testModel(modelId: string, testType?: string): Promise<{ response: string }> {
    const body: Record<string, string> = { model_id: modelId };
    if (testType) body.test_type = testType;
    const response = await this.client.post('/admin/models/test', body);
    return response.data;
  }

  async seedModels(): Promise<void> {
    await this.client.post('/admin/models/seed');
  }

  async searchBedrockModels(query: string): Promise<{ matches: Array<{ model_id: string; model_name: string; provider: string; input_cost_per_1k: number | null; output_cost_per_1k: number | null }>; total: number }> {
    const response = await this.client.post('/admin/models/search', { query });
    return response.data;
  }

  async getModelPricing(refresh?: boolean): Promise<{
    pricing: Record<string, { model_id: string; input_cost_per_1k: number | null; output_cost_per_1k: number | null }>;
    cached: boolean;
    unmatched_models?: Array<{ model_id: string; display_name: string }>;
    available_pricing_names?: string[];
  }> {
    const url = refresh ? '/admin/models/pricing?refresh=true' : '/admin/models/pricing';
    const response = await this.client.get(url);
    return response.data;
  }

  async listModelAssignments(): Promise<{ functions: ModelAssignmentRecord[]; total: number }> {
    const response = await this.client.get('/admin/model-assignments');
    return response.data;
  }

  async updateModelAssignment(functionId: string, data: Record<string, unknown>): Promise<void> {
    await this.client.put(`/admin/model-assignments/${encodeURIComponent(functionId)}`, data);
  }

  async getPostProcessors(): Promise<{ processors: Array<{ name: string; description: string; params: Record<string, { type: string; required: boolean; default?: unknown; description: string }> }> }> {
    const response = await this.client.get('/admin/post-processors');
    return response.data;
  }

  async getAdminAiConfigBundle(): Promise<{
    models: Array<{ model_id: string; display_name: string; enabled: boolean; model_type?: string; input_cost_per_1k?: number | null; output_cost_per_1k?: number | null }>;
    functions: ModelAssignmentRecord[];
    snippets: Array<{ prompt_name: string; prompt_version: string; prompt_state: string }>;
    pricing: Record<string, { model_id: string; input_cost_per_1k: number | null; output_cost_per_1k: number | null }>;
    post_processors: Array<{ name: string; description: string; params: Record<string, { type: string; required: boolean; default?: unknown; description: string }> }>;
  }> {
    const response = await this.client.get('/admin/ai-config');
    return response.data;
  }

  // Admin - Work Queue
  async listWorkQueue(): Promise<{ items: WorkQueueItem[]; total: number }> {
    const response = await this.client.get('/admin/work-queue');
    return response.data;
  }

  async createWorkQueueItem(data: { target_area: string; target_component: string; request_text: string; status?: string; request_type?: RequestType; risk_score?: number | null }): Promise<{ message: string }> {
    const response = await this.client.post('/admin/work-queue', data);
    return response.data;
  }

  async updateWorkQueueItem(requestId: string, updates: { status?: string; result_notes?: string; target_area?: string; target_component?: string; request_text?: string; request_type?: RequestType; risk_score?: number | null }): Promise<{ message: string }> {
    const response = await this.client.put(`/admin/work-queue/${requestId}`, updates);
    return response.data;
  }

  async deleteWorkQueueItem(requestId: string): Promise<{ message: string }> {
    const response = await this.client.delete(`/admin/work-queue/${requestId}`);
    return response.data;
  }

  async getWorkQueueSettings(): Promise<{ settings: { autofix_risk_threshold: number; error_monitor_interval?: number; error_monitor_expression?: string; error_monitor_error?: string } }> {
    const response = await this.client.get('/admin/work-queue/settings');
    return response.data;
  }

  async updateWorkQueueSettings(settings: { autofix_risk_threshold?: number; error_monitor_interval?: number }): Promise<{ message: string; settings: { autofix_risk_threshold: number; error_monitor_interval?: number }; eventbridge_error?: string }> {
    const response = await this.client.put('/admin/work-queue/settings', settings);
    return response.data;
  }

  // Admin - PDF Calibration
  async getPdfCalibration(): Promise<{ constants: Record<string, number>; defaults: Record<string, number>; overrides: Record<string, number> }> {
    const response = await this.client.get('/admin/pdf-calibration');
    return response.data;
  }

  async updatePdfCalibration(overrides: Record<string, number>): Promise<{ message: string }> {
    const response = await this.client.put('/admin/pdf-calibration', { overrides });
    return response.data;
  }

  async simulatePdfLayout(params: { url?: string; data?: Record<string, unknown>; overrides?: Record<string, number> }): Promise<{
    total_pages: number;
    total_content_height_in: number;
    usable_height_per_page_in: number;
    pages: Array<{ page_num: number; used_height_in: number; available_height_in: number; whitespace_in: number; utilization_pct: number; block_count: number }>;
    blocks: Array<{ type: string; section: string; height_in: number; total_height_in: number; keep_together: boolean; prunable: boolean; match_score: number; item_path: string; children?: Array<{ type: string; height_in: number; total_height_in: number; prunable: boolean; match_score: number; item_path: string }> }>;
    sections_found: string[];
    constants_used: Record<string, number>;
  }> {
    const response = await this.client.post('/admin/pdf-calibration/simulate', params);
    return response.data;
  }

  // Admin - Prompt Management
  async listPrompts(filters?: { prompt_name?: string; prompt_type?: string; prompt_state?: string }): Promise<{ prompts: any[]; total: number }> {
    const params = new URLSearchParams();
    if (filters?.prompt_name) params.append('prompt_name', filters.prompt_name);
    if (filters?.prompt_type) params.append('prompt_type', filters.prompt_type);
    if (filters?.prompt_state) params.append('prompt_state', filters.prompt_state);
    const response = await this.client.get(`/admin/prompts?${params.toString()}`);
    return response.data;
  }

  async getPromptVersions(promptName: string): Promise<{ prompts: any[] }> {
    const response = await this.client.get(`/admin/prompts/${encodeURIComponent(promptName)}`);
    return response.data;
  }

  async updatePrompt(promptName: string, data: Record<string, any>): Promise<any> {
    const response = await this.client.put(`/admin/prompts/${encodeURIComponent(promptName)}`, data);
    return response.data;
  }

  async deletePrompt(promptName: string): Promise<any> {
    const response = await this.client.delete(`/admin/prompts/${encodeURIComponent(promptName)}`);
    return response.data;
  }

  async deletePromptVersion(promptName: string, promptVersion: string): Promise<any> {
    const response = await this.client.delete(`/admin/prompts/${encodeURIComponent(promptName)}?prompt_version=${encodeURIComponent(promptVersion)}`);
    return response.data;
  }

  async seedPrompts(): Promise<any> {
    const response = await this.client.post('/admin/prompts/seed');
    return response.data;
  }

  async clearPromptCache(): Promise<{ message: string; timestamp: number }> {
    const response = await this.client.post('/admin/prompts/cache-clear');
    return response.data;
  }

  async getLogConfig(): Promise<{ config: { default_level: string; overrides: Record<string, string> }; functions: string[] }> {
    const response = await this.client.get('/admin/logs/config');
    return response.data;
  }

  async updateLogConfig(config: { default_level: string; overrides: Record<string, string> }): Promise<any> {
    const response = await this.client.put('/admin/logs/config', config);
    return response.data;
  }

  async queryLogs(params: Record<string, string | number>): Promise<{ logs: any[] }> {
    const response = await this.client.get('/admin/logs', { params });
    return response.data;
  }

  // Admin - Pricing
  async getAdminPricing(): Promise<{ pricing: PricingConfig; updated_at: string; updated_by: string }> {
    const response = await this.client.get('/admin/pricing');
    return response.data;
  }

  async updateAdminPricing(pricing: PricingConfig): Promise<{ message: string; pricing: PricingConfig }> {
    const response = await this.client.put('/admin/pricing', { pricing });
    return response.data;
  }

  // Admin - Global Settings
  async getAdminGlobalSettings(): Promise<{ settings: Array<{ setting_key: string; setting_value: any; updated_at?: string; updated_by?: string }> }> {
    const response = await this.client.get('/admin/global-settings');
    return response.data;
  }

  async updateAdminGlobalSetting(setting_key: string, setting_value: any): Promise<{ message: string; setting: any }> {
    const response = await this.client.put('/admin/global-settings', { setting_key, setting_value });
    return response.data;
  }

  async deleteAdminGlobalSetting(key: string): Promise<{ message: string }> {
    const response = await this.client.delete(`/admin/global-settings/${encodeURIComponent(key)}`);
    return response.data;
  }

  // Public pricing (no auth required)
  async getPublicPricing(): Promise<{ pricing: PricingConfig }> {
    const response = await this.client.get('/pricing');
    return response.data;
  }

  // Public setting by key (no auth required, whitelisted keys only)
  async getPublicSetting(key: string): Promise<{ setting_key: string; setting_value: any }> {
    const response = await this.client.get(`/settings/${encodeURIComponent(key)}`);
    return response.data;
  }

  async lookupUser(query: string): Promise<{ userid: string }> {
    const response = await this.client.get('/admin/users', { params: { search: query, limit: 1 } });
    const users = response.data?.users || [];
    if (users.length > 0) {
      return { userid: users[0].userid };
    }
    throw new Error('User not found');
  }

  // Application Answers
  async listApplicationAnswers(): Promise<ApplicationAnswersListResponse> {
    const response = await this.client.get('/answers');
    return response.data;
  }

  async createApplicationAnswer(
    question: string,
    answer: string,
    linked_jobids: string[] = [],
  ): Promise<ApplicationAnswer> {
    const response = await this.client.post('/answers', { question, answer, linked_jobids });
    return response.data.answer;
  }

  async updateApplicationAnswer(
    qaid: string,
    patch: Partial<Pick<ApplicationAnswer, 'question' | 'answer' | 'recommendations' | 'linked_jobids'>>,
  ): Promise<ApplicationAnswer> {
    const response = await this.client.put(`/answers/${qaid}`, patch);
    return response.data.answer;
  }

  async deleteApplicationAnswer(qaid: string): Promise<void> {
    await this.client.delete(`/answers/${qaid}`);
  }

  async cloneApplicationAnswer(qaid: string): Promise<ApplicationAnswer> {
    const response = await this.client.post(`/answers/${qaid}/clone`);
    return response.data.answer;
  }

  async getApplicationAnswerFeedback(qaid: string): Promise<ApplicationAnswer> {
    const response = await this.client.post(`/answers/${qaid}/feedback`);
    return response.data.answer;
  }

  // Interview Q&A
  async listInterviewQA(): Promise<InterviewListResponse> {
    const response = await this.client.get('/interview');
    return response.data;
  }

  async createInterviewQuestion(question: string): Promise<InterviewQuestion> {
    const response = await this.client.post('/interview/questions', { question });
    return response.data.question;
  }

  async deleteInterviewQuestion(qid: string): Promise<void> {
    await this.client.delete(`/interview/questions/${qid}`);
  }

  async updateInterviewAnswer(
    qid: string,
    patch: Partial<Pick<InterviewUserData, 'starred' | 'answer'>>,
  ): Promise<InterviewUserData> {
    const response = await this.client.put(`/interview/${qid}`, patch);
    return response.data.user_data;
  }

  async getInterviewFeedback(qid: string): Promise<InterviewUserData> {
    const response = await this.client.post(`/interview/${qid}/feedback`);
    return response.data.user_data;
  }

  // Admin - Email Templates (Communications)
  async listEmailTemplates(): Promise<{ templates: EmailTemplate[]; total: number }> {
    const response = await this.client.get('/admin/email-templates');
    return response.data;
  }

  async getEmailTemplate(templateKey: string): Promise<{ template: EmailTemplate }> {
    const response = await this.client.get(`/admin/email-templates/${encodeURIComponent(templateKey)}`);
    return response.data;
  }

  async updateEmailTemplate(
    templateKey: string,
    updates: Partial<Pick<EmailTemplate, 'subject' | 'body_html' | 'body_text' | 'variables'>>,
  ): Promise<{ template: EmailTemplate; message: string }> {
    const response = await this.client.put(`/admin/email-templates/${encodeURIComponent(templateKey)}`, updates);
    return response.data;
  }

  async deleteEmailTemplate(templateKey: string): Promise<{ message: string }> {
    const response = await this.client.delete(`/admin/email-templates/${encodeURIComponent(templateKey)}`);
    return response.data;
  }

  async seedEmailTemplates(): Promise<{ message: string; templates: string[] }> {
    const response = await this.client.post('/admin/email-templates/seed');
    return response.data;
  }

  // Admin - Per-user blocked_actions override
  async updateUserBlockedActions(
    userid: string,
    payload: {
      ongoing_action_keys: string[];
      until_cycle_action_keys: string[];
      auto_block_excessive: boolean;
      reason: string;
    },
  ): Promise<{
    blocked_actions: string[];
    blocked_actions_until_cycle: string[];
    auto_block_excessive: boolean;
    added: string[];
    removed: string[];
    message: string;
  }> {
    const response = await this.client.put(
      `/admin/users/${encodeURIComponent(userid)}/blocked-actions`,
      payload,
    );
    return response.data;
  }
}

// Final files generation response type
export interface GenerateFinalFilesResponse {
  message: string;
  files: {
    resume_html: string | null;
    resume_pdf: string | null;
    resume_txt: string | null;
    resume_template: string | null;
    cover_letter_html: string | null;
    cover_letter_pdf: string | null;
    cover_letter_txt: string | null;
  };
  s3_locations: {
    resume_html: string | null;
    resume_pdf: string | null;
    resume_txt: string | null;
    resume_template: string | null;
    cover_letter_html: string | null;
    cover_letter_pdf: string | null;
    cover_letter_txt: string | null;
  };
  phase: string;
}

// Pricing types
export interface PricingPlan {
  name: string;
  slug: string;
  tagline: string;
  display_price: string;
  price_cents: number;
  period: string;
  period_label: string;
  color: string;
  mono_label: string;
  cta_label: string;
  cta_url: string;
  cta_style: string;
  badge: string;
  badge_note?: string;
  features: string[];
  paused_features: string[];
  stripe_price_id?: string;
  sort_order: number;
  limits: {
    max_resumes?: number;
    monthly_jobs?: number;
    ai_enabled?: boolean;
  };
}

export interface PricingFaq {
  question: string;
  answer: string;
}

export interface PricingCssClass {
  class: string;
  description: string;
  example: string;
}

export interface PricingConfig {
  plans: Record<string, PricingPlan>;
  faq?: PricingFaq[];
  footer_note?: string;
  css_classes?: PricingCssClass[];
}

// Admin metrics types
export interface DynamoDBTableMetrics {
  item_count: number;
  size_bytes: number;
  status: string;
  error?: string;
}

export interface S3BucketMetrics {
  size_bytes: number;
  object_count: number;
  size_mb: number;
  error?: string;
}

export interface UserMetrics {
  total: number;
  by_role: Record<string, number>;
  by_status: Record<string, number>;
  by_plan: Record<string, number>;
  new_users_7d: number;
  new_users_30d: number;
  monthly_revenue?: number;
  annual_revenue_projected?: number;
  error?: string;
}

export interface CostEstimate {
  note: string;
  dynamodb: number;
  s3: number;
  lambda: number;
  api_gateway: number;
  cloudfront: number;
  cognito: number;
  bedrock: number;
  other?: number;
  total_estimate: number;
  currency: string;
  source?: 'cost_explorer' | 'fallback';
}

// Canary Analytics types (premortem failure-mode monitoring)
export type CanaryStatus = 'green' | 'amber' | 'red' | 'info';

export interface CanaryIngest {
  status: CanaryStatus;
  total_jobs: number;
  healthy: number;
  placeholder: number;
  research_in_progress: number;
  research_failed: number;
  placeholder_stuck_24h: number;
  ingest_success_rate: number | null;
  enrich_rate_limited: number;
  enrich_failed: number;
  new_jobs_7d: number;
  by_domain: Array<{ domain: string; total: number; failed: number; failure_rate: number }>;
  error?: string;
}

export interface CanaryOutcomes {
  status: CanaryStatus;
  total_user_jobs: number;
  by_phase: Record<string, number>;
  generated: number;
  applied: number;
  interviewing: number;
  accepted: number;
  errored: number;
  error_rate: number | null;
  pdf_generated: number;
  ready_to_applied_rate: number | null;
  applied_to_interview_rate: number | null;
  interview_to_accepted_rate: number | null;
  stalled_ready: number;
  stalled_ready_rate: number | null;
  error?: string;
}

export interface CanaryEconomics {
  status: CanaryStatus;
  active_users_30d: number;
  resumes_started_30d: number;
  bedrock_cost_30d: number | null;
  total_aws_cost_30d: number | null;
  cost_per_active_user: number | null;
  bedrock_per_active_user: number | null;
  ai_calls_30d: number | null;
  ai_failures_30d: number | null;
  ai_tokens_in_30d: number | null;
  ai_tokens_out_30d: number | null;
  error?: string;
}

export interface CanaryConversion {
  status: CanaryStatus;
  total_users: number;
  by_plan: Record<string, number>;
  paid_users: number;
  trial_users: number;
  cancelled_users: number;
  cancelled_30d: number;
  new_users_30d: number;
  paid_conversion_rate: number | null;
  moat_synonym_jobs: number;
  moat_skill_group_jobs: number;
  error?: string;
}

export interface CanaryTrust {
  status: CanaryStatus;
  open_errors: number;
  errors_by_source: Record<string, number>;
  errors_7d: number;
  high_risk_open: number;
  fabrication_signals: number;
  fabrication_signals_7d: number;
  error?: string;
}

export interface CanaryMetrics {
  generated_at: string;
  overall_status: CanaryStatus;
  ingest: CanaryIngest;
  outcomes: CanaryOutcomes;
  economics: CanaryEconomics;
  conversion: CanaryConversion;
  trust: CanaryTrust;
}

export interface GrowthTrends {
  total_jobs: number;
  new_jobs_7d: number;
  new_jobs_30d: number;
  total_resumes: number;
  error?: string;
}

export interface AdminMetricsResponse {
  timestamp: string;
  dynamodb: Record<string, DynamoDBTableMetrics>;
  s3: Record<string, S3BucketMetrics>;
  users: UserMetrics;
  cost_estimate: CostEstimate;
  growth: GrowthTrends;
  canaries?: CanaryMetrics;
}

// Model Assignment record from API (superset - includes function assignments and _cost_config)
export interface ModelAssignmentRecord {
  function_id: string;
  model_id?: string;
  description?: string;
  ai_pct?: number;
  token_in_formula?: string;
  token_out_formula?: string;
  // _cost_config fields
  job_desc?: number;
  resume?: number;
  small_section?: number;
  experience_section?: number;
  resume_tags?: number;
  job_tags?: number;
  jobs_per_month?: number;
  post_processors?: Array<{ fn: string; params: Record<string, unknown> }>;
  [key: string]: unknown;
}

// AI Metrics types
export interface MetricDatapoint {
  timestamp: string;
  count: number;
}

export interface AiMetricsResponse {
  invocations: Record<string, MetricDatapoint[]>;
  errors: Record<string, MetricDatapoint[]>;
  bedrock_cost_30d: number | null;
  timestamp: string;
}

// AI Usage tracking types
export interface AiUsageDailyPoint {
  date: string;
  calls: number;
  failures?: number;
  users: number;
}

export interface AiUsageModelData {
  total_calls: number;
  avg_calls_per_day: number;
  active_users: number;
  daily: AiUsageDailyPoint[];
}

export interface AiUsagePromptData {
  total_calls: number;
  avg_calls_per_day: number;
  total_failures: number;
  avg_failures_per_day: number;
  active_users: number;
  daily: AiUsageDailyPoint[];
}

export interface AiUsageResponse {
  models: Record<string, AiUsageModelData>;
  prompts: Record<string, AiUsagePromptData>;
  start_date: string;
  end_date: string;
  days_in_range: number;
}

// Admin User Detail types
export interface AdminUserJobSummary {
  jobid: string;
  jobphase: string;
  jobtitle?: string;
  jobcompany?: string;
  createdAt?: string;
}

export interface AdminUserResumeSummary {
  resumename: string;
  lastupdate?: string;
}

export interface AdminUserDetail {
  user: Record<string, string | number | boolean | undefined>;
  job_count: number;
  resume_count: number;
  jobs_summary: AdminUserJobSummary[];
  resumes_summary: AdminUserResumeSummary[];
  summary_limit?: number;
}

/** Stripe billing summary. `has_payment: null` means UNKNOWN (see `error`) —
 *  render the error, never a default. */
export interface AdminUserBilling {
  has_payment: boolean | null;
  error?: string;
  subscription_status?: string;
  amount_cents?: number | null;
  currency?: string | null;
  interval?: string | null;
  price_id?: string | null;
  plan_name?: string;
  current_period_end?: number | null;
  cancel_at_period_end?: boolean;
}

export interface AdminUserStats30d {
  window_start: string;
  window_end: string;
  days_active: number;
  jobs_applied: number;
  ai_calls: number;
  resume_edits: number;
  /** Earliest date the day-granular counters reach; null = tracking starts today. */
  tracked_since: string | null;
  error?: string;
}

export interface AdminUserDetailExtended extends AdminUserDetail {
  billing?: AdminUserBilling;
  stats_30d?: AdminUserStats30d;
}

// Admin User List types
export interface AdminUserListItem {
  userid: string;
  email: string;
  userhandle: string;
  role: string;
  status: string;
  plan: string;
  createdAt: string;
  org_id?: string;
}

// Marketing data types (API response shapes)
export interface MarketingLinkData {
  label: string;
  url: string;
}

export interface MarketingTaskData {
  item_id: string;
  item_type: 'task';
  parent_id: string;
  title: string;
  description: string;
  status: string;
  due_date: string;
  execution_type: string;
  priority: string;
  links: MarketingLinkData[];
  notes: string;
  sort_order: number;
}

export interface MarketingObjectiveData {
  item_id: string;
  item_type: 'objective';
  parent_id: string;
  title: string;
  description: string;
  status: string;
  due_date: string;
  execution_type: string;
  tasks: MarketingTaskData[];
  sort_order: number;
}

export interface MarketingCampaignData {
  item_id: string;
  item_type: 'campaign';
  title: string;
  description: string;
  status: string;
  start_date: string;
  end_date: string;
  target_metrics: string;
  objectives: MarketingObjectiveData[];
  total_tasks: number;
  completed_tasks: number;
  percent_complete: number;
  sort_order: number;
}

// Marketing types
export interface MarketingItemPayload {
  item_type: string;
  parent_id?: string;
  title: string;
  description?: string;
  due_date?: string;
  priority?: string;
  execution_type?: string;
  status?: string;
  [key: string]: string | undefined;
}

// Work Queue types
export type RequestType = 'Error' | 'Tweak' | 'Request' | 'Enhancement' | 'Feedback';

export interface WorkQueueItem {
  request_id: string;
  created_at: string;
  status: string;
  target_area: string;
  target_component: string;
  request_text: string;
  completed_at: string;
  result_notes: string;
  created_by: string;
  request_type?: RequestType;
  risk_score?: number | null;
  source?: string;
  error_signature?: string;
}

// Email template types
export interface EmailTemplate {
  template_key: string;
  subject: string;
  body_html: string;
  body_text: string;
  variables?: string[];
  updated_at?: string;
  updated_by?: string;
}

// Action keys mirrored from shared/usage_meter.py THRESHOLDS
export const BLOCKABLE_ACTION_KEYS = [
  'resume_import',
  'resume_review',
  'job_add',
  'forwarded_email',
  'qa_add',
  'qa_review',
  'generate_resume',
] as const;

// Per-month soft thresholds — mirrors THRESHOLDS in shared/usage_meter.py.
// Keep in sync.
export const ACTION_THRESHOLDS: Record<string, number> = {
  resume_import: 20,
  resume_review: 20,
  job_add: 500,
  forwarded_email: 200,
  qa_add: 100,
  qa_review: 100,
  generate_resume: 500,
};

export type BlockableActionKey = (typeof BLOCKABLE_ACTION_KEYS)[number];

export const api = new ApiService();
